import React, { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import type { HostConfig, HostConfigOverrides } from '@/host-config/domain/HostConfig';
import {
  HostCompanyLogoSessionProvider,
  useHostCompanyLogoSession,
  useHostCompanyLogoSessionState
} from '@/host-config/presentation/HostCompanyLogoSession';

import { DEFAULT_HOST_CONFIG } from './defaults';

void React;

type AnyRecord = Record<string, unknown>;

function isPlainObject(value: unknown): value is AnyRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deepMerge<T extends object>(base: T, override: unknown): T {
  if (!isPlainObject(override)) return base;

  const out: AnyRecord = { ...(base as AnyRecord) };
  for (const [key, overrideValue] of Object.entries(override)) {
    // Hosts should omit keys; if a host explicitly passes `undefined`, don't override required base fields.
    if (overrideValue === undefined) continue;

    const baseValue = (base as AnyRecord)[key];

    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      out[key] = deepMerge(baseValue, overrideValue);
      continue;
    }

    out[key] = overrideValue;
  }

  return out as T;
}

const HostConfigContext = createContext<HostConfig>(DEFAULT_HOST_CONFIG);

const defaultSessionSetter = (): void => undefined;

export interface HostConfigProviderProps {
  overrides?: HostConfigOverrides;
}

function HostConfigProviderInner({ children, overrides }: PropsWithChildren<HostConfigProviderProps>) {
  const { refreshToken, sessionPreviewUrl, setSessionPreviewUrl, suppressHostLogo, setSuppressHostLogo, bumpRefreshToken } =
    useHostCompanyLogoSessionState();

  const mergedOverrides = useMemo((): HostConfigOverrides | undefined => {
    if (!overrides) return undefined;

    const next: HostConfigOverrides = { ...overrides };
    if (overrides.onUploadLogo) {
      const upload = overrides.onUploadLogo;
      next.onUploadLogo = async (file: File) => {
        await upload(file);
        bumpRefreshToken();
      };
    }
    return next;
  }, [bumpRefreshToken, overrides]);

  const value = useMemo<HostConfig>(() => deepMerge(DEFAULT_HOST_CONFIG, mergedOverrides), [mergedOverrides]);

  const inner = mergedOverrides ? <HostConfigContext.Provider value={value}>{children}</HostConfigContext.Provider> : children;

  return (
    <HostCompanyLogoSessionProvider
      refreshToken={refreshToken}
      sessionPreviewUrl={sessionPreviewUrl}
      setSessionPreviewUrl={setSessionPreviewUrl}
      suppressHostLogo={suppressHostLogo}
      setSuppressHostLogo={setSuppressHostLogo}
    >
      {inner}
    </HostCompanyLogoSessionProvider>
  );
}

export function HostConfigProvider({ children, overrides }: PropsWithChildren<HostConfigProviderProps>) {
  const parentSession = useHostCompanyLogoSession();
  const isNestedSession = parentSession.setSessionPreviewUrl !== defaultSessionSetter;

  // Federated host already owns session + overrides; inner RemoteRoutes must not shadow them.
  if (isNestedSession && !overrides) {
    return <>{children}</>;
  }

  return <HostConfigProviderInner overrides={overrides}>{children}</HostConfigProviderInner>;
}

export function useHostConfig(): HostConfig {
  return useContext(HostConfigContext);
}
